/**
 * Rendered markdown with clickable, line-anchored blocks — the review viewer.
 *
 * Same safety posture as `Markdown.tsx` (react-markdown + remark-gfm, NO
 * rehype-raw, ever — employee-authored documents quote external text), plus
 * one addition: every block-level element is wrapped in a clickable region
 * that knows its source-line range (react-markdown's `node.position`).
 * Clicking a block selects `{line_start, line_end, quoted_text}` for the
 * comment composer; blocks that already carry comments get a margin marker.
 *
 * Nested blocks (a paragraph inside a blockquote) are all clickable — the
 * innermost wins via stopPropagation, which is exactly the finer-grained
 * anchor a reviewer wants. The composer renders inside the one block whose
 * type+range key matches the active selection, so it appears exactly once.
 *
 * **The `components` map is a module constant, and the live values reach the
 * blocks through context.** React reconciles by component *identity*: a map
 * rebuilt per render hands React a new type at every position, which is an
 * unmount+remount of the whole document instead of an update. That destroyed
 * the composer's textarea on every keystroke (cursor snapped back to 0) and
 * would have discarded a half-written comment on each 30 s poll. Keep the map
 * out of the render body — context changes re-render the blocks, which is all
 * that is wanted, without touching their identity.
 */
import clsx from "clsx";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";

import { useRemarkPlugins } from "../lib/hooks/useRemarkPlugins";
import { markdownUrlTransform } from "../lib/markdownUrls";
import { sectionForLine, type ReviewSection } from "../lib/reviewSections";
import { LinkAnchor } from "./LinkAnchor";

export interface BlockRange {
  line_start: number;
  line_end: number;
  quoted_text: string;
  /** type:start:end — identifies the exact block for composer placement. */
  key: string;
}

/**
 * Section folding, threaded through the same context as everything else.
 * Collapse hides blocks via CSS (`display: none`) — never an unmount — so
 * composer state, `data-line-*` queryability, and a half-written draft all
 * survive a fold; anchors keep referring to the full, unsliced source.
 */
export interface ReviewFolding {
  sections: ReviewSection[];
  collapsedIds: Set<string>;
  onToggle: (id: string) => void;
  /** Current-version comment count per section id (badge on collapsed heads). */
  commentCounts: Map<string, number>;
  /** New-side lines changed vs. the previous version, when diff data exists. */
  changedLines?: Set<number> | null;
}

/** Source slice for an anchored range, 1-based inclusive lines. */
function sliceLines(contentMd: string, start: number, end: number): string {
  return contentMd
    .split("\n")
    .slice(Math.max(0, start - 1), end)
    .join("\n");
}

/** Whether any line in [start, end] is in the set. */
function rangeHasAny(lines: Set<number>, start: number, end: number): boolean {
  for (let line = start; line <= end; line++) {
    if (lines.has(line)) return true;
  }
  return false;
}

interface Props {
  contentMd: string;
  /** Called when the reviewer clicks a block; null-op ⇒ read-only viewer. */
  onSelect?: (range: BlockRange) => void;
  /** The block currently being commented on (its `key`). */
  activeKey?: string | null;
  /** Rendered inside the active block, under its content (the composer). */
  activeExtra?: ReactNode;
  /** Lines that carry at least one comment — marks the owning blocks. */
  commentedLines?: Set<number>;
  /** Collapsible sections; absent ⇒ no folding (read-only/diff views). */
  folding?: ReviewFolding;
}

const BLOCK_TAGS = [
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "ul",
  "ol",
  "pre",
  "blockquote",
  "table",
] as const;

/** What each block needs from the viewer, without owning its identity. */
const BlockContext = createContext<Props>({ contentMd: "" });

function buildBlock(tag: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function Block({ node, children, ...rest }: any) {
    const {
      contentMd,
      onSelect,
      activeKey,
      activeExtra,
      commentedLines,
      folding,
    } = useContext(BlockContext);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Tag = tag as any;
    const inner =
      tag === "table" ? (
        <div className="md-table-wrap">
          <table {...rest}>{children}</table>
        </div>
      ) : (
        <Tag {...rest}>{children}</Tag>
      );

    const start: number | undefined = node?.position?.start?.line;
    const end: number | undefined = node?.position?.end?.line ?? start;
    if ((!onSelect && !folding) || !start || !end) return inner;

    const key = `${tag}:${start}:${end}`;
    const active = key === activeKey;
    const hasComment = commentedLines
      ? rangeHasAny(commentedLines, start, end)
      : false;
    const changed = folding?.changedLines
      ? rangeHasAny(folding.changedLines, start, end)
      : false;

    const section = folding ? sectionForLine(folding.sections, start) : undefined;
    const collapsed =
      !!section && !!folding && folding.collapsedIds.has(section.id);

    // The section heading itself: the primary click toggles the fold; the
    // hover-revealed ＋ keeps heading-line commenting reachable in both fold
    // states. stopPropagation discipline matches the composer/anchor pattern.
    if (folding && section && start === section.headingLine) {
      const count = folding.commentCounts.get(section.id) ?? 0;
      return (
        <div
          className={clsx(
            "review-block review-section-block",
            hasComment && "review-block--commented",
            changed && "review-block--changed",
            active && "review-block--active",
          )}
          data-line-start={start}
          data-line-end={end}
        >
          <div
            className={clsx(
              "review-section-head",
              collapsed && "review-section-head--closed",
            )}
            onClick={(e) => {
              e.stopPropagation();
              folding.onToggle(section.id);
            }}
          >
            <span className="review-chevron" aria-hidden="true">
              ▾
            </span>
            <div className="review-head-title">{inner}</div>
            {collapsed && count > 0 ? (
              <span className="review-badge tnum">
                {count} comment{count === 1 ? "" : "s"}
                {changed ? " · changed" : ""}
              </span>
            ) : collapsed && changed ? (
              <span className="review-badge">changed</span>
            ) : null}
            {onSelect ? (
              <button
                type="button"
                className={clsx(
                  "review-head-comment btn btn-sm btn-ghost",
                  active && "review-head-comment--active",
                )}
                title="Comment on this heading"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect({
                    line_start: start,
                    line_end: end,
                    quoted_text: sliceLines(contentMd, start, end),
                    key,
                  });
                }}
              >
                ＋
              </button>
            ) : null}
          </div>
          {active ? activeExtra : null}
        </div>
      );
    }

    return (
      <div
        className={clsx(
          "review-block",
          hasComment && "review-block--commented",
          changed && "review-block--changed",
          active && "review-block--active",
          collapsed && "review-block--folded",
        )}
        data-line-start={start}
        data-line-end={end}
        onClick={
          onSelect
            ? (e) => {
                e.stopPropagation();
                onSelect({
                  line_start: start,
                  line_end: end,
                  quoted_text: sliceLines(contentMd, start, end),
                  key,
                });
              }
            : undefined
        }
      >
        {inner}
        {active ? activeExtra : null}
      </div>
    );
  };
}

function Anchor({ href, children }: { href?: string; children?: ReactNode }) {
  return (
    // A link inside a clickable block: follow the link, don't anchor.
    <LinkAnchor href={href} onClick={(e) => e.stopPropagation()}>
      {children}
    </LinkAnchor>
  );
}

// Built once at module scope — the identity react-markdown reconciles against.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COMPONENTS: Record<string, any> = { a: Anchor };
for (const tag of BLOCK_TAGS) COMPONENTS[tag] = buildBlock(tag);

export function ReviewMarkdown({
  contentMd,
  onSelect,
  activeKey,
  activeExtra,
  commentedLines,
  folding,
}: Props) {
  // The blocks are permanently mounted; the live values reach them through
  // context, so a changed selection or a new draft re-renders them without
  // altering their identity.
  const ctx = useMemo(
    () => ({ contentMd, onSelect, activeKey, activeExtra, commentedLines, folding }),
    [contentMd, onSelect, activeKey, activeExtra, commentedLines, folding],
  );
  // Memoized per text: the pipeline re-runs only when the document or the
  // resolved ID links change.
  const remarkPlugins = useRemarkPlugins(contentMd);

  return (
    <BlockContext.Provider value={ctx}>
      <div className="md-block review-doc">
        <ReactMarkdown
          remarkPlugins={remarkPlugins}
          components={COMPONENTS}
          urlTransform={markdownUrlTransform}
        >
          {contentMd}
        </ReactMarkdown>
      </div>
    </BlockContext.Provider>
  );
}
